import { useState } from 'react';
import {
  Box, Typography, Chip, Button, Tabs, Tab, Grid, Paper, Stack,
} from '@mui/material';
import { CheckCircle, HourglassEmpty, ErrorOutline } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { withSerialNo } from '../../components/ui/gridColumns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import StatCard from '../../components/ui/StatCard';
import { formatMoney } from '../../utils/currency';
import {
  getSettlements, getSettlementSummary, retrySettlement, Settlement, SettlementSummaryResponse,
} from '../../services/business/settlementService';

const STATUS_ICONS = {
  settled: <CheckCircle />,
  pending: <HourglassEmpty />,
  failed: <ErrorOutline />,
} as const;

const VERTICALS = [
  { key: '', label: 'All' },
  { key: 'transportation', label: 'Transport' },
  { key: 'porter', label: 'Porter' },
  { key: 'on_demand', label: 'Provider' },
];

const STATUS_FILTERS = [
  { key: '', label: 'All statuses' },
  { key: 'settled', label: 'Settled' },
  { key: 'pending', label: 'Pending' },
  { key: 'processing', label: 'Processing' },
  { key: 'failed', label: 'Failed' },
  { key: 'on_hold', label: 'On hold' },
];

const statusColor: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  settled: 'success',
  paid: 'success',
  pending: 'warning',
  processing: 'info',
  failed: 'error',
  on_hold: 'default',
};

function settlementStatusLabel(status: string): string {
  if (status === 'paid') return 'settled';
  if (status === 'processing') return 'processing';
  return status;
}

export default function SettlementsPage() {
  const queryClient = useQueryClient();
  const [vertical, setVertical] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data: summaryRes } = useQuery({
    queryKey: ['settlement-summary'],
    queryFn: getSettlementSummary,
  });

  const summary = summaryRes as SettlementSummaryResponse | undefined;
  const byStatus = summary?.byStatus || [];
  const byModule = summary?.byModule || [];

  const { data, isLoading } = useQuery({
    queryKey: ['settlements', vertical, status, page],
    queryFn: () => getSettlements({
      status: status || undefined,
      module: vertical || undefined,
      page: page + 1,
      limit: pageSize,
    }),
  });

  const retryMutation = useMutation({
    mutationFn: retrySettlement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      queryClient.invalidateQueries({ queryKey: ['settlement-summary'] });
    },
  });

  const statusMap = Object.fromEntries(byStatus.map((s) => [s._id, s]));
  const moduleMap = Object.fromEntries(byModule.map((m) => [m._id, m]));

  const columns: GridColDef[] = [
    { field: 'resourceNumber', headerName: 'Reference', width: 140 },
    { field: 'module', headerName: 'Module', width: 110 },
    { field: 'partnerType', headerName: 'Partner', width: 120 },
    {
      field: 'partnerId', headerName: 'Payee', width: 160,
      valueGetter: (_v, row) => {
        const p = row.partnerId as Settlement['partnerId'];
        if (!p || typeof p !== 'object') return '—';
        return [p.firstName, p.lastName].filter(Boolean).join(' ') || p.email || '—';
      },
    },
    { field: 'grossAmount', headerName: 'Gross', width: 90, valueFormatter: (v, row) => `${formatMoney(v, row)}` },
    { field: 'commissionAmount', headerName: 'Commission', width: 100, valueFormatter: (v, row) => `${formatMoney(v, row)}` },
    { field: 'payoutAmount', headerName: 'Payout', width: 90, valueFormatter: (v, row) => `${formatMoney(v, row)}` },
    {
      field: 'status', headerName: 'Status', width: 110,
      renderCell: (params) => (
        <Chip
          label={settlementStatusLabel(params.value as string)}
          color={statusColor[params.value as string] || 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions', headerName: '', width: 100, sortable: false,
      renderCell: (params) =>
        params.row.status === 'failed'
        || params.row.status === 'pending'
        || params.row.status === 'processing' ? (
          <Button size="small" onClick={() => retryMutation.mutate(params.row._id as string)} disabled={retryMutation.isPending}>
            Retry
          </Button>
        ) : null,
    },
  ];

  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance"
        title="Commission Settlements"
        subtitle="Partner commission settlements for Transport, Porter, and Provider (on-demand)."
      />

      <Grid container spacing={2} mb={3}>
        {(['settled', 'pending', 'failed'] as const).map((key) => (
          <Grid item xs={12} sm={4} key={key}>
            <StatCard
              title={key}
              value={statusMap[key]?.count ?? 0}
              hint={`Payout ${formatMoney((statusMap[key]?.totalPayout ?? 0))}`}
              color={statusColor[key] === 'default' ? '#64748b' : undefined}
              icon={STATUS_ICONS[key]}
            />
          </Grid>
        ))}
      </Grid>

      <Typography variant="subtitle2" fontWeight={700} mb={1}>By service</Typography>
      <Grid container spacing={2} mb={3}>
        {VERTICALS.filter((v) => v.key).map(({ key, label }) => {
          const mod = moduleMap[key];
          return (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Paper
                sx={{ p: 2, cursor: 'pointer', border: vertical === key ? 2 : 1, borderColor: vertical === key ? 'primary.main' : 'divider' }}
                onClick={() => { setVertical(key); setPage(0); }}
              >
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h5" fontWeight={800}>{mod?.count ?? 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatMoney((mod?.totalPayout ?? 0))} payout
                </Typography>
                <Box display="flex" gap={0.5} mt={1}>
                  <Chip label={`${mod?.settled ?? 0} ok`} size="small" color="success" variant="outlined" />
                  <Chip label={`${mod?.pending ?? 0} pend`} size="small" color="warning" variant="outlined" />
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Tabs value={vertical} onChange={(_, v) => { setVertical(v); setPage(0); }} sx={{ mb: 1.5 }}>
        {VERTICALS.map((v) => <Tab key={v.key || 'all'} label={v.label} value={v.key} />)}
      </Tabs>

      <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
        {STATUS_FILTERS.map((s) => (
          <Chip
            key={s.key || 'all-status'}
            label={s.label}
            clickable
            color={status === s.key ? 'primary' : 'default'}
            variant={status === s.key ? 'filled' : 'outlined'}
            onClick={() => { setStatus(s.key); setPage(0); }}
            size="small"
          />
        ))}
      </Stack>

      <DataGrid
        rows={data?.data || []}
        columns={withSerialNo(columns)}
        loading={isLoading}
        getRowId={(r) => r._id}
        autoHeight
        paginationMode="server"
        rowCount={data?.meta?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m) => setPage(m.page)}
        pageSizeOptions={[pageSize]}
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />
    </PageShell>
  );
}
